import { InputAdornment } from '@mui/material'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { v4 as uuidv4 } from 'uuid'
import { AssigneeEntity } from '@omnea-app/core/blocks'
import { isValidEmail } from '@omnea-app/packages/universal/general/isValidEmail'
import { AssigneeTypeEnum } from '@omnea-app/packages/universal/hasura/graphql'
import { AssigneeSourceType, useAssigneeOptions } from './AssigneeAutocomplete'
import { AssigneeAutocompleteTextField } from './components/AssigneeAutocompleteTextField'
import { AssigneeAvatar } from './components/AssigneeAvatar'
import { BaseAssigneeAutocomplete, getAssigneeLabel } from './components/BaseAssigneeAutocomplete'
import { AssigneesError } from './types'

interface Props {
  value: AssigneeEntity | null
  onChange: (newValue: AssigneeEntity | null) => void
  title?: string
  label?: string
  required?: boolean
  disabled?: boolean
  additionalOptions?: AssigneeEntity[]
  type?: AssigneeSourceType
  error?: AssigneesError
  fullWidth?: boolean
  placeholder?: string
  onBlur?: () => void
  helperText?: string
  excludeUserId?: string
  onlyAdditionalOptions?: boolean
  allowedAssignees?: AssigneeEntity[]
  onAddNew?: () => void
  addNewText?: string
  groupBy?: string
  hiddenAssigneeIds?: string[]
  clearAfterSelection?: boolean
  minSearchLength?: number
}

export const SingleAssigneeAutocomplete = ({
  type = AssigneeSourceType.USERS_GROUPS,
  value,
  allowedAssignees,
  onlyAdditionalOptions,
  additionalOptions,
  excludeUserId,
  hiddenAssigneeIds,
  onChange,
  fullWidth,
  groupBy,
  disabled,
  onAddNew,
  addNewText,
  placeholder,
  title,
  label,
  required,
  onBlur,
  error,
  helperText,
  clearAfterSelection = false,
  minSearchLength,
}: Props) => {
  const [inputValue, setInputValue] = useState(() => (value ? getAssigneeLabel(value) : ''))
  const currentValueIds = value ? [value.id] : []
  const { options } = useAssigneeOptions({
    allowedAssignees,
    onlyAdditionalOptions,
    additionalOptions,
    excludeUserId,
    hiddenAssigneeIds,
    type,
    currentValueIds,
    searchQuery: inputValue,
    minSearchLength,
  })

  const [isOpen, setIsOpen] = useState(false)
  const { t } = useTranslation('workflow-builder')
  const handleChange = (_: unknown, item: AssigneeEntity | string | null) => {
    if (item === null) {
      onChange(null)
      return
    }
    const newValueEntity = typeof item === 'string' ? { value: item, type: AssigneeTypeEnum.EMAIL, id: uuidv4() } : item
    onChange(newValueEntity.type !== AssigneeTypeEnum.EMAIL || isValidEmail(newValueEntity.value) ? newValueEntity : null)

    // Clear input after selection if requested
    if (clearAfterSelection) {
      setInputValue('')
    }
  }

  return (
    <BaseAssigneeAutocomplete<false>
      options={options}
      open={isOpen}
      setIsOpen={setIsOpen}
      multiple={false}
      fullWidth={fullWidth}
      value={value}
      groupBy={groupBy}
      hideGroups={type !== AssigneeSourceType.USERS_GROUPS}
      disabled={disabled}
      onChange={handleChange}
      onAddNew={onAddNew}
      addNewText={addNewText}
      inputValue={inputValue}
      onInputChange={setInputValue}
      renderInput={(params) => (
        <AssigneeAutocompleteTextField
          {...params}
          label={label}
          required={required}
          placeholder={!value ? placeholder || t('config-assignee-placeholder') : ''}
          startAdornment={
            value ? (
              <InputAdornment position="start">
                <AssigneeAvatar option={value} />
              </InputAdornment>
            ) : undefined
          }
          data-testid={`single-assignee-autocomplete-${title}`}
          onBlur={onBlur}
          error={error}
          helperText={helperText}
          onTabKeyDown={(newValue) => handleChange(null, newValue)}
        />
      )}
    />
  )
}
